import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { Spinner } from '@gbv/ui';
import { useAuth } from './stores/auth';
import { AdminShell } from './shell/AdminShell';
import { LoginPage } from './pages/LoginPage';

const OverviewPage = lazy(() => import('./pages/OverviewPage'));
const EmployeesPage = lazy(() => import('./pages/EmployeesPage'));
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const AssignmentsPage = lazy(() => import('./pages/AssignmentsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AdminAccountsPage = lazy(() => import('./pages/AdminAccountsPage'));
const AuditPage = lazy(() => import('./pages/AuditPage'));
const SignInsPage = lazy(() => import('./pages/SignInsPage'));
const ClientVaultPage = lazy(() => import('./pages/ClientVaultPage'));
const ClientRecordsPage = lazy(() => import('./pages/ClientRecordsPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));

function FullPageSpinner() {
  return (
    <div className="grid min-h-screen place-items-center bg-surface-page text-ink-soft">
      <Spinner size="lg" />
    </div>
  );
}

function RequireAuth() {
  const status = useAuth((s) => s.status);
  const restore = useAuth((s) => s.restore);

  useEffect(() => {
    if (status === 'unknown') void restore();
  }, [status, restore]);

  if (status === 'unknown') return <FullPageSpinner />;
  if (status === 'anonymous') return <Navigate to="/login" replace />;

  return (
    <AdminShell>
      <Suspense fallback={<FullPageSpinner />}>
        <Outlet />
      </Suspense>
    </AdminShell>
  );
}

function RedirectIfAuthenticated() {
  const status = useAuth((s) => s.status);
  const restore = useAuth((s) => s.restore);

  useEffect(() => {
    if (status === 'unknown') void restore();
  }, [status, restore]);

  if (status === 'unknown') return <FullPageSpinner />;
  if (status === 'authenticated') return <Navigate to="/" replace />;
  return <LoginPage />;
}

export const router = createBrowserRouter([
  { path: '/login', element: <RedirectIfAuthenticated /> },
  {
    path: '/',
    element: <RequireAuth />,
    children: [
      { index: true, element: <OverviewPage /> },
      { path: 'employees', element: <EmployeesPage /> },
      { path: 'clients', element: <ClientsPage /> },
      { path: 'clients/:id/vault', element: <ClientVaultPage /> },
      { path: 'clients/:id/records', element: <ClientRecordsPage /> },
      { path: 'assignments', element: <AssignmentsPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'admins', element: <AdminAccountsPage /> },
      { path: 'audit', element: <AuditPage /> },
      { path: 'sign-ins', element: <SignInsPage /> },
      { path: 'account', element: <AccountPage /> },
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
